import { cn } from "../lib/utils";
import { A_JUGER, JUGEMENTS, jugementEnClair, LIBELLE_JUGEMENT } from "./coloriage-textes";
import type { Jugement } from "./contract";

/**
 * Les marques de jugement du module : le point d'une version, sa legende, le badge.
 *
 * Les trois couleurs sont des tokens de `theme.css` (section 5), jamais les couleurs en dur
 * des planches. Les classes sont ecrites en entier : Tailwind ne garde que les noms de classe
 * qu'il lit tels quels dans le source. Rien ne repose sur la couleur seule : le point porte
 * son jugement en infobulle, la legende l'ecrit, le badge aussi.
 */

type Ton = { readonly point: string; readonly badge: string };

const TONS: Readonly<Record<Jugement, Ton>> = {
  bonne: { point: "bg-jugement-bonne", badge: "bg-jugement-bonne/15 text-jugement-bonne" },
  defaut: { point: "bg-jugement-defaut", badge: "bg-jugement-defaut/15 text-jugement-defaut" },
  a_jeter: { point: "bg-jugement-jeter", badge: "bg-jugement-jeter/15 text-jugement-jeter" },
};

/** Une version sans jugement : un point creux. */
const CREUX = "border-[1.5px] border-muted-foreground bg-card";

/** Le point d'une version : plein et colore une fois jugee, creux avant. */
export function PointJugement({
  jugement,
  titre,
  courant = false,
  grand = false,
}: {
  jugement: Jugement | null;
  /** L'infobulle : `version 1 : Bonne`. Sans titre, le point est decoratif. */
  titre?: string;
  /** La version affichee : un anneau autour du point. */
  courant?: boolean;
  grand?: boolean;
}) {
  return (
    <span
      aria-hidden={titre === undefined ? "true" : undefined}
      className={cn(
        "inline-block shrink-0 rounded-full",
        grand ? "size-[11px]" : "size-[9px]",
        jugement === null ? CREUX : TONS[jugement].point,
        courant && "outline-2 outline-offset-2 outline-foreground",
      )}
      title={titre}
    />
  );
}

/** L'infobulle d'un point : `version 2 : Défaut`, `version 3 : à juger`. */
export function titrePoint(essai: number, jugement: Jugement | null): string {
  return `version ${essai} : ${jugement === null ? A_JUGER : LIBELLE_JUGEMENT[jugement]}`;
}

/** Les points des versions d'une page, dans l'ordre des numeros. */
export function PointsDesVersions({
  versions,
  courante,
  grand = false,
}: {
  versions: readonly { id: number; essai: number; jugement: Jugement | null }[];
  courante?: number;
  grand?: boolean;
}) {
  return (
    <span className={cn("flex flex-wrap items-center", grand ? "gap-2" : "gap-[5px]")}>
      {versions.map((version) => (
        <PointJugement
          courant={version.id === courante}
          grand={grand}
          jugement={version.jugement}
          key={version.id}
          titre={titrePoint(version.essai, version.jugement)}
        />
      ))}
    </span>
  );
}

/** La legende des points : les trois jugements, puis la version qui attend. */
export function LegendeJugements({ children }: { children?: string }) {
  return (
    <p className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
      {[...JUGEMENTS, null].map((jugement) => (
        <span className="flex items-center gap-1.5" key={jugement ?? "a_juger"}>
          <PointJugement jugement={jugement} />
          {jugementEnClair(jugement)}
        </span>
      ))}
      {children === undefined ? null : <span>{children}</span>}
    </p>
  );
}

/** Le badge d'un jugement : `Bonne`, `Défaut`, `À jeter`, ou `à juger` en gris. */
export function BadgeJugement({ jugement }: { jugement: Jugement | null }) {
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold",
        jugement === null ? "bg-muted text-muted-foreground" : TONS[jugement].badge,
      )}
    >
      {jugement === null ? A_JUGER : LIBELLE_JUGEMENT[jugement]}
    </span>
  );
}

/** Les tons de badge d'un etat de page ou de cahier, par la couleur du jugement qu'il evoque. */
export const BADGE_VERT = TONS.bonne.badge;
export const BADGE_ROUGE = TONS.a_jeter.badge;
export const BADGE_AMBRE = TONS.defaut.badge;
